import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function EmailSignupPopup({ config, onDismiss, onSubmit, submitting, isLoggedIn }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = () => {
    if (!email.trim()) return false;
    if (config.show_name_field && !name.trim()) return false;
    if (config.email_consent_text && !consent) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    const result = await onSubmit({ email, name });
    if (result !== false) setSubmitted(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        key="popup-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
      >
        <motion.div
          key="popup-card"
          initial={{ scale: 0.92, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 30 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
          className="relative bg-[#17121f] border border-[#26202e] rounded-2xl w-[380px] max-w-[calc(100vw-32px)] flex flex-col items-center overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            <div
              className="absolute bottom-0 left-0 right-0 h-[350px]"
              style={{ backgroundImage: "linear-gradient(270deg, #17121f 2.2%, #590fc0 31.5%, #75018c 68%, #17121f 97.2%)" }}
            />
            <div className="absolute inset-0 backdrop-blur-[50px] bg-[rgba(11,4,21,0.5)]" />
          </div>

          <button
            onClick={onDismiss}
            className="absolute top-3 right-3 z-20 text-white/40 hover:text-white transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {submitted ? (
            <div className="relative z-10 flex flex-col items-center gap-4 py-10 px-6 text-center w-full">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.1 }}
                className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center"
              >
                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </motion.div>
              <h3 className="text-white text-xl font-semibold">
                {config.success_message || "You're on the list!"}
              </h3>
              <p className="text-white/50 text-sm">Check your inbox for confirmation.</p>
            </div>
          ) : (
            <>
              <div className="relative z-10 w-full pt-8 px-6 pb-0 text-center">
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center mb-3"
                >
                  <div className="w-12 h-12 rounded-full bg-purple-600/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-300" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                </motion.div>
                <h2 className="text-[22px] font-semibold text-white leading-tight">
                  {config.title || "Stay in the loop"}
                </h2>
                {config.subtitle && (
                  <p className="text-white/50 text-[14px] mt-1.5">{config.subtitle}</p>
                )}
              </div>

              <div className="relative z-10 w-full px-6 pt-5 pb-1 space-y-3">
                {config.show_name_field && (
                  <input
                    className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
                <input
                  className="w-full bg-white/5 border border-white/10 focus:border-purple-500/60 rounded-lg px-4 py-3 text-white text-[14px] placeholder-white/30 outline-none transition-all"
                  placeholder="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {config.email_consent_text && (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/10 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-white/40 text-[11px] leading-relaxed">{config.email_consent_text}</span>
                  </label>
                )}
              </div>

              <div className="relative z-10 w-full px-6 pt-2 pb-6 space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !canSubmit()}
                  className="w-full bg-gradient-to-b from-[#7e25f7] to-[#590fc0] border border-[#7e25f7] text-white font-bold text-[15px] py-3.5 px-6 rounded-xl transition-all duration-200 hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2"
                >
                  {submitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  )}
                  {config.button_text || "Sign Up"}
                </button>
                <button
                  onClick={onDismiss}
                  className="w-full text-center text-[13px] text-white/40 hover:text-white/70 transition-colors py-1"
                >
                  No thanks
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
